// Client-side TTS helpers. Mirrors the server's chunker (apps/api/src/lib/
// tts-chunk.ts) so narration can be prefetched and played chunk by chunk.

/** A chunk plus its character span in the source text (for read-along highlight). */
export interface TtsSegment {
  text: string;
  start: number;
  end: number;
}

/** The chunk size each engine can render without being cut off. */
export function ttsChunkMax(serverAvailable: boolean): number {
  return serverAvailable ? 320 : 180;
}

/**
 * Split text into sentence-bounded chunks AND report each chunk's character span
 * in the (CRLF-normalized) input, so the editor can highlight what's being read.
 * Offsets are not shifted by a leading trim; each chunk's text is trimmed but its
 * span covers the untrimmed sentence run.
 */
export function chunkForTtsSegments(input: string, maxLen = 3500): TtsSegment[] {
  const text = input.replace(/\r\n/g, "\n");
  if (!text.trim()) return [];

  const sentenceRe = /[^.!?]*[.!?]+["')\]]*\s*|[^.!?]+$/g;
  const sentences: { s: string; start: number }[] = [];
  let m: RegExpExecArray | null;
  while ((m = sentenceRe.exec(text)) !== null) {
    if (m[0].length === 0) {
      sentenceRe.lastIndex++; // guard against a zero-length match stalling
      continue;
    }
    sentences.push({ s: m[0], start: m.index });
  }

  const segs: TtsSegment[] = [];
  let curText = "";
  let curStart = -1;
  let curEnd = -1;
  const flush = () => {
    const trimmed = curText.trim();
    if (trimmed) segs.push({ text: trimmed, start: curStart, end: curEnd });
    curText = "";
    curStart = -1;
    curEnd = -1;
  };

  for (const sen of sentences) {
    if (sen.s.length > maxLen) {
      flush();
      for (let i = 0; i < sen.s.length; i += maxLen) {
        const piece = sen.s.slice(i, i + maxLen);
        const trimmed = piece.trim();
        if (trimmed) segs.push({ text: trimmed, start: sen.start + i, end: sen.start + i + piece.length });
      }
      continue;
    }
    if (curText.length + sen.s.length > maxLen && curText.trim()) flush();
    if (curStart === -1) curStart = sen.start;
    curText += sen.s;
    curEnd = sen.start + sen.s.length;
  }
  flush();
  return mergeUnspeakableSegs(segs);
}

/**
 * Fold any segment with no speakable character (e.g. a lone "." at a sentence
 * boundary) into a neighbor, extending its span. A neural TTS engine errors on
 * input it can't phonemize, so a punctuation-only chunk is never sent alone.
 * Mirrors the server chunker's mergeUnspeakable.
 */
function mergeUnspeakableSegs(segs: TtsSegment[]): TtsSegment[] {
  const speakable = /[A-Za-z0-9]/;
  const out: TtsSegment[] = [];
  for (const seg of segs) {
    const prev = out[out.length - 1];
    if (prev && !speakable.test(seg.text)) {
      prev.text = `${prev.text} ${seg.text}`.trim();
      prev.end = Math.max(prev.end, seg.end);
    } else {
      out.push({ ...seg });
    }
  }
  if (out.length > 1 && !speakable.test(out[0]!.text)) {
    out[1]!.text = `${out[0]!.text} ${out[1]!.text}`.trim();
    out[1]!.start = Math.min(out[0]!.start, out[1]!.start);
    out.shift();
  }
  return out;
}

export function chunkForTts(text: string, maxLen = 3500): string[] {
  return chunkForTtsSegments(text, maxLen).map((s) => s.text);
}

export interface TtsChunkResult {
  url: string;
  /** The provider id the server actually used (e.g. "blueprint", "openai"). */
  providerId: string | null;
  /** The provider's human label, for the UI (e.g. "Blueprint (Chatterbox)"). */
  providerLabel: string | null;
  /** The actual engine that produced the audio (e.g. "chatterbox-turbo", "kokoro"). */
  engine: string | null;
}

/** Synthesize one chunk to an object URL (caller revokes it when done). */
export async function synthesizeTtsChunk(projectId: string, text: string, voice: string): Promise<TtsChunkResult> {
  const response = await fetch(`/api/projects/${projectId}/tts`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ text, voice })
  });
  if (!response.ok) {
    let message = `Narration failed (${response.status})`;
    try {
      message = ((await response.json()) as { message?: string }).message ?? message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }
  const providerId = response.headers.get("X-TTS-Provider");
  const providerLabel = response.headers.get("X-TTS-Provider-Label");
  const engine = response.headers.get("X-TTS-Engine");
  const blob = await response.blob();
  return { url: URL.createObjectURL(blob), providerId, providerLabel, engine };
}
